import { cx } from '@emotion/css';
import { htmlEscape } from '@snapchat/core';
import type { FC } from 'react';
import { useContext, useEffect } from 'react';
import type { RouteProps } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import { Config } from '../../config';
import { ElementLocation } from '../../context/ElementLocationContext/ElementLocationContext';
import { PageLayoutContext } from '../../context/PageLayoutContext';
import { logWarning } from '../../helpers/logging';
import { Feature, useFeatureFlags } from '../FeatureFlags';
import { SideNavigationShallow } from '../SideNavigation';
import {
  minHeightCss,
  slugContainerCss,
  slugContainerWithSideNavCss,
  slugGutterCss,
} from './Slug.styles';
import { SlugCollection } from './SlugCollection';
import { useSlugToNavMap } from './useSlugToNavMap';

export const Slug: FC<RouteProps> = () => {
  const location = useLocation();
  // NOTE: We don't use the useParams() hook here because we want to check
  // the pathname exactly. Otherwise we run into issues like "///foo" not matching
  // routes like "/:slug*", and router thinking it should be a homepage match.
  // See https://jira.sc-corp.net/browse/WEBP-9117
  const originalSlug = location.pathname.substring(1);
  const slug = originalSlug === '' ? 'home' : htmlEscape(originalSlug);
  const featureFlags = useFeatureFlags();

  // TODO: Delete once we no longer need this functionality, after snapchat
  // home is fully migrated to consumer web.
  const shouldRedirectHome = featureFlags[Feature.HOME_REDIRECT] === 'true';

  useEffect(() => {
    if (shouldRedirectHome && originalSlug === 'home' && Config.isClient) {
      const url = new URL(window.location.href);
      url.pathname = '';
      window.history.replaceState(null, '', url); // or pushState
    }
  }, [originalSlug, shouldRedirectHome]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll to the top of the slug on every slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const { sideNavMap, subNavMap } = useSlugToNavMap();

  const hasSideNav = sideNavMap.has(slug);
  const sideNavId = sideNavMap.get(slug)?.sys.id;
  const hasSubNav = subNavMap.has(slug);
  const subNavId = subNavMap.get(slug)?.sys.id;

  const { setHasSideNav, setHasSubNav } = useContext(PageLayoutContext);

  useEffect(() => {
    setHasSideNav?.(hasSideNav);
    setHasSubNav?.(hasSubNav);
  }, [hasSideNav, hasSubNav, setHasSideNav, setHasSubNav]);

  if (sideNavId && subNavId) {
    logWarning({
      component: 'Slug',
      message: `Having both a SideNav and a SubNav isn't supported.`,
      context: { slug },
    });
  }

  // The <main> is set in index.ejs
  return (
    <div
      data-testid="mwp-slug"
      className={cx(slugContainerCss, minHeightCss, slugGutterCss, {
        [slugContainerWithSideNavCss]: hasSideNav,
      })}
    >
      {sideNavId && (
        <ElementLocation.Provider value={{ elementLocation: 'SideNavigation' }}>
          <SideNavigationShallow sys={{ id: sideNavId }} />
        </ElementLocation.Provider>
      )}
      <SlugCollection key={slug} slug={slug} />
    </div>
  );
};
